import EventEmitter from "eventemitter3";
import {defaultError, WebError} from "@hybird/core/error";
import {ProxyMethod} from "@hybird/core/proxy";
import {deviceError} from "@hybird/device/deviceError";

const defaultResult = {code: 0, success: true, data: []};

class DeviceBeginSealType {
  static normal = 1;
  static remote = 2;
  static continuous = 3;
}

class DeviceProtocolOption {
  /**
   * 设备连接前
   */
  onConnect;

  /**
   * 设备连接后
   */
  onConnected;

  /**
   * 设备重连
   */
  onReconnect;

  /**
   * 设备连接异常
   */
  onError;
  onDisconnect;
  onDisconnected;
  config;

  /**
   * @type{Class<DeviceProtocolRequest>}
   */
  request
}

/**
 *   onFingerprintIdentify;
 *   onFingerprintIdentifyResult;
 *   onBeginSealed(camera, record, analyze, infrared);
 *    camera: 是否开启摄像头
 *    record: 是否录制视频
 *    analyze: 是否开启范围扫描
 *    infrared: 是否开启红外扫描
 *   onSealAuthentication (remote:Integer)  remote 用印模式
 *   onDocumentStateUploading;
 *   onRemoteAudit;
 *   onDeviceIn;
 *   onDeviceOut;
 */
class DeviceProtocol extends EventEmitter {
  online = false;

  /**
   * @type {DeviceConnectProvide}
   */
  _deviceConnectProvide;

  /**
   * @type{DeviceProtocolOption}
   */
  deviceProtocolOption;

  /**
   * @type{DeviceInfo}
   */
  _deviceInfo;
  _connected = false;

  /**
   * @type{DeviceProtocolBeginSeal}
   * @private
   */
  _deviceProtocolBeginSeal;

  /**
   * @type{DeviceProtocolOperation}
   * @private
   */
  _deviceProtocolManage;

  /**
   * @type{BleSend| BleSendV2 | MqttSend| SerialSendV2}
   */
  send;

  /**
   * @type{DeviceProtocolRequest}
   */
  request;

  get version() {
    return 1;
  }

  get connected() {
    return this._connected && this._deviceConnectProvide.connected;
  }

  /**
   *
   */
  set deviceInfo(deviceInfo) {
    if (this.deviceProtocolBeginSeal) {
      if (this.deviceProtocolBeginSeal.isBeginSeal) {
        this.deviceProtocolBeginSeal.endSeal({force: true, type: 6}).finally();
      }
    }

    this._deviceInfo = deviceInfo;
  }

  addProxy(method, interceptor) {
    this._proxy = this._proxy ? this._proxy : {};
    this._addProxy(this._proxy, this, method, interceptor)
  }

  addBeginSealProxy(method, interceptor) {
    this._proxyBeginSeal = this._proxyBeginSeal ? this._proxyBeginSeal : {};
    this._addProxy(this._proxyBeginSeal, this._deviceProtocolBeginSeal, method, interceptor)
  }

  addOperationProxy(method, interceptor) {
    this._proxyOperation = this._proxyOperation ? this._proxyOperation : {};
    this._addProxy(this._proxyOperation, this._deviceProtocolManage, method, interceptor)
  }

  _addProxy(proxy, target, method, interceptor) {
    if (!proxy[method]) {
      //当不存在时, 创建默认类, 绕过创建机制
      if (!target) {
        target = target ? target : {};
        if (!target[method]) {
          target[method] = async () => {
          };
        }
      }

      proxy[method] = new ProxyMethod(target, method);
    }

    proxy[method].addInterceptor(interceptor);
  }

  _resetBeginSealProxy() {
    const tmp = this._proxyBeginSeal;
    this._proxyBeginSeal = {};

    for (let i in tmp) {
      this.addBeginSealProxy(i, tmp[i].interceptors);
    }
  }

  _resetOperationProxy() {
    const tmp = this._proxyOperation;
    this._proxyOperation = {};

    for (let i in tmp) {
      this.addOperationProxy(i, tmp[i].interceptors);
    }
  }

  /**
   *
   * @return {DeviceInfo}
   */
  get deviceInfo() {
    return this._deviceInfo;
  }

  /**
   * @return {DeviceProtocolBeginSeal}
   */
  get deviceProtocolBeginSeal() {
    return this._deviceProtocolBeginSeal;
  }

  /**
   *
   * @return {DeviceConnectProvide}
   */
  get deviceConnectProvide() {
    return this._deviceConnectProvide;
  }

  /**
   * @abstract
   * @return{DeviceProtocolBeginSeal}
   */
  _createDeviceProtocolBeginSeal(option = {}) {
    return new DeviceProtocolBeginSeal(this._deviceInfo, this, option);
  }

  /**
   * @abstract
   * @return{DeviceProtocolOperation}
   */
  _createDeviceProtocolOperation() {
    return new DeviceProtocolOperation(this._deviceInfo, this);
  }

  /**
   *
   * @param deviceProvide{DeviceProvide}
   * @param deviceInfo
   * @param deviceProtocolOption{DeviceProtocolOption}
   */
  constructor(deviceProvide, deviceInfo, deviceProtocolOption = {}) {
    super();
    this.deviceProvide = deviceProvide;
    this._deviceInfo = deviceInfo;
    this.deviceProtocolOption = deviceProtocolOption;
    this._deviceConnectProvide = this.deviceProvide.deviceConnectProvide;

    if (deviceProtocolOption.onConnect) {
      this.on(DeviceEvent.CONNECT, deviceProtocolOption.onConnect);
    }

    if (deviceProtocolOption.onConnected) {
      this.on(DeviceEvent.CONNECTED, deviceProtocolOption.onConnected);
    }

    if (deviceProtocolOption.onError) {
      this.on(DeviceEvent.ERROR, deviceProtocolOption.onError);
    }

    if (deviceProtocolOption.onReconnect) {
      this.on(DeviceEvent.RECONNECT, deviceProtocolOption.onReconnect);
    }

    if (deviceProtocolOption.onDisconnect) {
      this.on(DeviceEvent.DISCONNECT, deviceProtocolOption.onDisconnect);
    }

    if (deviceProtocolOption.onDisconnected) {
      this.on(DeviceEvent.DISCONNECTED, deviceProtocolOption.onDisconnected);
    }

    if (deviceProtocolOption.request) {
      this.request = new deviceProtocolOption.request(this);
    }
  }

  get isBeginSeal() {
    return this.deviceProtocolBeginSeal?.isBeginSeal || this._deviceProtocolManage?.isBeginSeal;
  }

  set connectType(connectType) {
    this._connectType = connectType;
  }

  get connectType() {
    if (this._connectType) {
      return this._connectType;
    } else {
      return this.deviceInfo.connectType;
    }
  }

  async connect({success, complete, message, fail, timeout = 30, config} = {}) {
    if (config) {
      this.config = config
    }

    await this.deviceProvide.connect(this._deviceInfo, {
      //当时多组合时, 未连接的情况使用指定的连接方式
      connectType: this.connectType, timeout, onConnect: params => {
        this.emit(DeviceEvent.CONNECT, params);
      }, onError: params => {
        this.emit(DeviceEvent.ERROR, params);
      }, onReconnect: params => {
        this.emit(DeviceEvent.RECONNECT, params);
      }, onDisconnect: params => {
        this.emit(DeviceEvent.DISCONNECT, params);
      }, onDisconnected: (params = {}) => {
        if (!params.type) {
          params.type = 3;
        }
        this.emit(DeviceEvent.DISCONNECTED, params);
      }, config: this.config
    });

    try {
      this.emit(DeviceEvent.CONNECTED, {connectType: this.connectType});

      //TODO 是否被其他人使用中
      //TODO 获取地理位置信息

      this.emit(DeviceEvent.BEFORE_USER_LOGIN);
      await this._connect({success, complete, message, fail, timeout: 10, config})

      this._connected = true;
      this.emit(DeviceEvent.USER_LOGIN);
    } catch (e) {
      await this.send.unsubscribe();
      await this.deviceProvide.disconnect().catch(e => {
        console.error("连接关闭, 关闭连接失败", e);
      });

      if (deviceError.isError(e, deviceError.SUBSCRIBE_ONE_READ_TIMEOUT)) {
        throw WebError.code(deviceError.SUBSCRIBE_ONE_READ_TIMEOUT, {
          msg: "连接设备超时"
        });
      } else {
        throw e;
      }
    }

    return this;
  }

  async canConnect() {
    return true;
  }

  async close() {
    await this._deviceConnectProvide.close(true).catch(e => {
      console.warn("关闭通道失败", e);
    })
  }

  async disconnect({logout = false, type = 1, connectType} = {}) {
    console.error(`退出登录相关状态, connect=${this._connected}, deviceConnectProvide.connect=${this.deviceConnectProvide.connected}, deviceInfo=${JSON.stringify(this.deviceInfo)}`)
    console.log(`退出登录的方法状态, type=${type}, logout=${logout}`)

    if (connectType && connectType !== this.deviceConnectProvide.connectType) {
      console.warn(`异常退出情况, type=${type}, connectType=${connectType}, this.deviceConnectProvide.connectType=${this.deviceConnectProvide.connectType}`)
      return;
    }

    if (this._connected) {
      this._connected = false;
      await this._disconnect({type: type, logout}).catch(e => {
        console.warn("关闭失败", e);
      });

      this.emit(DeviceEvent.USER_LOGOUT, {type, logout, connectType});
    }

    if (this._deviceConnectProvide.connected || logout) {
      await this._deviceConnectProvide.disconnect().catch(e => {
        console.warn("关闭通道失败", e);
      })
    }
  }

  exception({data}) {
    switch (parseInt(data.code)) {
      case 100004:
        return WebError.code(deviceError.DEVICE_USE_NOT_CONNECT, {data});
      case 100005:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "设备协议不存在, 请检查设备版本",
        });
      case 100006:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "设备电机运行中, 请稍后下发",
        });
      case 100007:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "设备磁盘溢出, 请等待设备上传",
        });
      case 100008:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "指纹录入超时",
        });
      case 100009:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "镜像下载中, 请等待设备下载",
        });
      case 100010:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "设备协议异常, 请重试",
        });
      case 100011:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data, msg: "设备指纹同步中， 禁止登录",
        });
      default:
        return WebError.code(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS, {
          data,
        });
    }
  }

  /**
   *
   * @param option{DeviceProtocolBeginSealOption}
   * @return {Promise<DeviceProtocolBeginSeal>}
   */
  async beginSeal({option} = {}) {
    if (!this.deviceConnectProvide.connected) {
      throw WebError.code(defaultError.NOT_CONNECT);
    }

    //是否有可用印次数
    if (this.deviceInfo.leftTimes <= 0) {
      throw WebError.code(defaultError.USED_UP_THE_NUMBER_OF_TIMES);
    }

    //是否有录入指纹
    if (this.deviceInfo.authentication === 1 && (!this.deviceInfo.position || this.deviceInfo.position.length === 0)) {
      throw WebError.code(defaultError.NO_FINGERPRINT_RECORDED);
    }

    if (!this._deviceProtocolBeginSeal || !this._deviceProtocolBeginSeal.isBeginSeal) {
      const beginSealOption = Object.assign(DeviceProtocolBeginSealOption.getInstance(this.deviceInfo), option);
      this._deviceProtocolBeginSeal = this._createDeviceProtocolBeginSeal(beginSealOption);
      this._resetBeginSealProxy();
    } else {
      this._deviceProtocolBeginSeal.option = Object.assign(DeviceProtocolBeginSealOption.getInstance(this.deviceInfo), option);
    }

    return this._deviceProtocolBeginSeal;
  }

  async operation() {
    if (!this._deviceProtocolManage) {
      this._deviceProtocolManage = this._createDeviceProtocolOperation();
      this._resetOperationProxy();
    }

    await this._deviceProtocolManage.beginSeal();
    return this._deviceProtocolManage;
  }

  /**
   *
   * @return {Promise<DeviceProtocolBeginSeal>}
   * @param type
   * @param force
   * @param logout
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   */
  async endSeal({type, force = false, logout, success, complete, message, fail, timeout} = {}) {
    if (this._deviceProtocolBeginSeal) {
      await this._deviceProtocolBeginSeal.endSeal({type, force, logout, success, complete, message, fail, timeout});
    }

    if (this._deviceProtocolManage) {
      await this._deviceProtocolManage.endSeal({type, force, logout, success, complete, message, fail, timeout});
    }

    this.deviceInfo.clearExamine();
  }

  async _connect({success, complete, message, fail, timeout} = {}) {
  }

  async _disconnect({type, logout, success, complete, message, fail, timeout} = {}) {
  }

  async deviceStatus() {
    return this._deviceStatus();
  }

  async _deviceStatus() {
    return false;
  }

  async sync() {
    await this.setDeviceConfig({config: this.deviceInfo});
    await this.precord();
    await this._sync()
  }

  /**
   * 同步指纹等数据
   * @return {Promise<{code,data,success}>} 无返回值
   * @private
   */
  async _sync(data = {}) {
    this.emit(DeviceEvent.SYNC_STATUS, {progress: 1, type: 1, data: defaultResult});
    return defaultResult;
  }

  async precord() {
    return this._precord();
  }

  async _precord() {
    this.emit(DeviceEvent.SYNC_STATUS, {progress: 1, type: 2, data: []});
  }

  /**
   *
   * @param config {{
   *       offTime:int,
   *       sleepTime: int,
   *       voice: int,
   *       sealPattern: int,
   *       gyroParameter: int,
   *       useWifi: int,
   *       useBluetooth: int,
   *       useMobileNetwork: int,
   *       lock: int}|DeviceInfo}
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async setDeviceConfig({config, success, complete, message, fail, timeout}) {
    const defaultConfig = {
      offTime: 5,
      sleepTime: 3,
      voice: 1,
      sealPattern: 1,
      useWifi: 1,
      useBluetooth: 1,
      useMobileNetwork: 1,
      wifiPriority: 1,
      gyroParameter: 10,
      sealId: null,
      sealType: 1,
      sealName: null,
      remoteWake: 1,
      needConvertVideo: 1
    }

    config = Object.assign(defaultConfig, config);
    return this._setDeviceConfig({config, success, complete, message, fail, timeout});
  }

  async _setDeviceConfig({config, success, complete, message, fail, timeout}) {
  }
}

class CompositeDeviceProtocol extends DeviceProtocol {

  /**
   *
   * @type {DeviceProtocol}
   * @private
   */
  _deviceProtocol;

  constructor(deviceProvide, deviceInfo, deviceProtocolOption) {
    super(deviceProvide, deviceInfo, deviceProtocolOption);
  }

  set connectType(connectType) {
    if (this._deviceProtocol) {
      this._deviceProtocol.connectType = connectType;
    }
  }

  get connectType() {
    return this._deviceProtocol?.connectType ? this._deviceProtocol?.connectType : this.deviceInfo.connectType;
  }

  async canConnect() {
    return await this._deviceProtocol?.canConnect() ?? true;
  }

  async close() {
    await this.deviceConnectProvide.close(true).catch(e => {
      console.warn("关闭通道失败", e);
    });
  }

  async deviceStatus() {
    return this._deviceProtocol?.deviceStatus();
  }

  async sync() {
    return this._deviceProtocol?.sync();
  }

  async precord() {
    return this._deviceProtocol?.precord();
  }

  /**
   * @abstract
   * @return number 1: 同时执行  2: 等待执行
   */
  get connectPromiseType() {
    return 2;
  }

  /**
   * @abstract
   * @return {{1:DeviceProtocol}}
   */
  get deviceProtocols() {
    return {};
  }

  get connected() {
    return this._deviceProtocol != null ? this._deviceProtocol.connected : false;
  }

  set deviceInfo(deviceInfo) {
    super.deviceInfo = deviceInfo;

    if (this._deviceProtocol) {
      this._deviceProtocol.deviceInfo = deviceInfo;
    }

    for (const i in this.deviceProtocols) {
      /**
       * @type{DeviceProtocol}
       */
      const deviceProtocol = this.deviceProtocols[i];
      deviceProtocol.deviceInfo = deviceInfo;
    }
  }

  get deviceInfo() {
    return super.deviceInfo;
  }

  get deviceProtocolBeginSeal() {
    return this._deviceProtocol != null ? this._deviceProtocol.deviceProtocolBeginSeal : null;
  }

  get deviceConnectProvide() {
    return super.deviceConnectProvide;
  }

  async connect({success, complete, message, fail, timeout, config} = {}) {
    try {
      let connectType = this.deviceInfo.connectType;
      let error;
      const connectTypes = []

      if (this._deviceProtocol?.connected) {
        return this._deviceProtocol;
      }

      let deviceProtocols = [];

      if (this.deviceInfo.connectType === 3) {
        //可以设备为列表, 当设置列表后按列表顺序进行连接
        if (!(this.deviceInfo.connectTypes instanceof Array)) {
          this.deviceInfo.connectTypes = [4, 2, 1];
        }

        for (const i in this.deviceInfo.connectTypes) {
          const tempConnectType = this.deviceInfo.connectTypes[i];
          if (tempConnectType != 3) {
            const deviceProtocol = this.deviceProtocols[tempConnectType];

            if (deviceProtocol) {
              deviceProtocols.push(deviceProtocol);
            }
          }
        }
      } else {
        const deviceProtocol = this.deviceProtocols[connectType];

        if (deviceProtocol) {
          deviceProtocols.push(deviceProtocol);
        }
      }

      for (const i in deviceProtocols) {
        //清理当前所有订阅, 防止冲突
        this.removeAllListeners();

        const deviceProtocol = new deviceProtocols[i](this.deviceProvide, this.deviceInfo, this.deviceProtocolOption);

        if (connectType !== 3 && deviceProtocol.connectType !== connectType) {
          continue;
        }

        if (connectTypes.includes(connectType)) {
          continue;
        }


        //使用统一的emit, 防止出现错误的监听
        deviceProtocol.emit = this.emit.bind(this);
        deviceProtocol.once = this.once.bind(this);
        deviceProtocol.on = this.on.bind(this);
        deviceProtocol.off = this.off.bind(this);
        deviceProtocol.addListener = this.addListener.bind(this);
        deviceProtocol.removeListener = this.removeListener.bind(this);
        deviceProtocol.removeAllListeners = this.removeAllListeners.bind(this);
        deviceProtocol.eventNames = this.eventNames.bind(this);
        deviceProtocol.listeners = this.listeners.bind(this);
        deviceProtocol.listenerCount = this.listenerCount.bind(this);

        const result = await this.tryConnect(deviceProtocol, {
          findDevice: () => {
            connectTypes.push(deviceProtocol.connectType);
          }, success, complete, message, fail, timeout: timeout, config
        }).then(connect => {
          return {
            deviceProtocol: deviceProtocol, connect
          }
        }).catch(e => {
          if (defaultError.isError(e, 11003)) {
            throw e;
          }

          console.warn("CompositeDeviceProtocol 连接异常", e);

          if (error == null || defaultError.isError(e)) {
            error = e;
          }

          return {
            deviceProtocol: deviceProtocol
          }
        })

        if (result.connect) {
          this._deviceProtocol = result.deviceProtocol;

          return result.deviceProtocol;
        }
      }
      if (!error) {
        error = WebError.code(defaultError.CONNECT_NOT_SUPPORT)
      }
      throw error;
    } finally {
      if (this._deviceProtocol) {
        for (let name in this._deviceProtocol) {
          if (this._deviceProtocol instanceof Function && !this[name]) {
            this[name] = () => {
              return this._deviceProtocol != null ? this._deviceProtocol[name].apply(this._deviceProtocol, arguments) : null;
            }
          }

          this._installProxy(this._proxy, this._deviceProtocol.addProxy.bind(this._deviceProtocol))
          this._installProxy(this._proxyBeginSeal, this._deviceProtocol.addBeginSealProxy.bind(this._deviceProtocol))
          this._installProxy(this._proxyOperation, this._deviceProtocol.addOperationProxy.bind(this._deviceProtocol))
        }
      }
    }
  }

  _installProxy(proxy, fn) {
    for (const name in proxy) {
      const interceptors = proxy[name].interceptors;

      for (const i in interceptors) {
        fn(name, interceptors[i]);
      }
    }
  }

  /**
   *
   * @param deviceProtocol{DeviceProtocol}
   * @param findDevice
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @param config
   * @return {Promise<*>}
   */
  async tryConnect(deviceProtocol, {findDevice, success, complete, message, fail, timeout, config} = {}) {
    if (deviceProtocol != null && await deviceProtocol.canConnect()) {
      findDevice();
      return await deviceProtocol.connect({success, complete, message, fail, timeout, config});
    }

    throw WebError.code(defaultError.CAN_NOT_FIND_DEVICE);
  }

  async disconnect({logout, type} = {}) {
    return this._deviceProtocol != null ? this._deviceProtocol.disconnect({
      logout, type
    }) : null;
  }

  async beginSeal({option, success, complete, message, fail, timeout} = {}) {
    return this._deviceProtocol != null ? this._deviceProtocol.beginSeal({
      option, success, complete, message, fail, timeout
    }) : null;
  }

  async operation() {
    return this._deviceProtocol != null ? this._deviceProtocol.operation() : null;
  }

  async endSeal({type, force = false, logout, success, complete, message, fail, timeout} = {}) {
    return this._deviceProtocol != null ? this._deviceProtocol.endSeal({
      type, force, logout, success, complete, message, fail, timeout
    }) : null;
  }

  _createDeviceProtocolBeginSeal(option) {
    return this._deviceProtocol != null ? this._deviceProtocol._createDeviceProtocolBeginSeal(option) : null;
  }

  _createDeviceProtocolOperation() {
    return this._deviceProtocol != null ? this._deviceProtocol._createDeviceProtocolOperation() : null;
  }
}

export class DeviceProtocolBeginSealOption {
  authentication = 2;
  sealAuthentication = 2;
  documentId;
  sealId;
  leftTimes;
  /**
   * 本次使用的盖印次数
   */
  currStartLeftTimes;
  position;
  remote = 1;
  pictureType = 2;
  videoType = 2;
  businessKey = "";
  staffId;
  infrared = 2;
  coverId = 0;
  hasCover = false;

  /**
   * ocr模式(1. 整份文件拍摄盖印 2. 单页拍摄盖印)
   * @type{Number}
   */
  ocrPattern = 2;

  /**
   * 印章识别 1、开启 2、关闭
   * @type{Number}
   */
  sealDiscern = 2;

  /**
   * 指纹认证的超时时间
   */
  fingerprintIdentifyTimeout = 60 * 1000;

  /**
   * 在连续用印是否自动伸出印章
   * @type {boolean}
   */
  autoSealStretch = true;

  /**
   *
   * @param option{Object | DeviceInfo}
   * @return {DeviceProtocolBeginSealOption}
   */
  static getInstance(option) {
    const beginSealOption = new DeviceProtocolBeginSealOption();
    beginSealOption.documentId = option.documentId;
    beginSealOption.sealId = option.sealId;
    beginSealOption.leftTimes = option.leftTimes;
    beginSealOption.currStartLeftTimes = option.leftTimes;
    beginSealOption.position = option.position;
    beginSealOption.remote = option.remote;
    beginSealOption.pictureType = option.pictureType;
    beginSealOption.equipmentRec = option.equipmentRec;
    beginSealOption.videoType = option.videoType;
    beginSealOption.infrared = option.infrared;
    beginSealOption.hasCover = option.hasCover;
    beginSealOption.coverId = option.coverId;
    beginSealOption.sealDiscern = option.sealDiscern;

    beginSealOption.authentication = option.authentication;
    beginSealOption.sealAuthentication = option.sealAuthentication;
    beginSealOption.ocrPattern = option.ocrPattern;
    return beginSealOption;
  }
}

class DeviceProtocolBeginSeal {
  /**
   * @type{DeviceProtocolBeginSealOption}
   */
  option;

  /**
   * @type {DeviceProtocol | MqttDeviceProtocol | MqttBleDeviceProtocol | BleDeviceProtocol}
   * @private
   */
  deviceProtocol;

  /**
   * @type {DeviceInfo}
   * @private
   */
  deviceInfo;

  _isBeginSeal = false;

  _remoteLockStatus = false;

  beforeLockTime;

  set remoteLockStatus(remoteLock) {
    this._remoteLockStatus = remoteLock;

    this.deviceProtocol.emit(DeviceEvent.REMOTE_LOCK, {
      documentId: this.deviceInfo.documentId, remoteLock
    });
  }

  get remoteLockStatus() {
    return this._remoteLockStatus
  }


  /**
   * @type {{
   *   equipmentLock: boolean,
   *   isExamine: boolean,
   *   mode: number,
   *   data: {
   *      picture: String,
   *   }
   * }}
   */
  _context = {};

  /**
   * @return {{
   *   camera: boolean,
   *   record: boolean,
   *   analyze: boolean,
   *   infrared: boolean,
   *   equipmentLock: boolean,
   *   isExamine: boolean,
   *   mode: number,
   *   data: {
   *      picture: any[],
   *   }
   * }}
   */
  get context() {
    return Object.assign({
      camera: this.option.videoType === 1 || (this.option.infrared === 2 && this.option.sealDiscern === 1),
      record: this.option.videoType === 1,
      analyze: this.option.sealDiscern === 1,
      infrared: this.option.infrared === 1
    }, this._context);
  }

  get isBeginSeal() {
    return this._isBeginSeal;
  }

  onFn = [];

  /**
   *
   * @param deviceInfo{DeviceInfo}
   * @param deviceProtocol{DeviceProtocol}
   * @param option{DeviceProtocolBeginSealOption}
   */
  constructor(deviceInfo, deviceProtocol, option) {
    this.deviceProtocol = deviceProtocol;
    this.deviceInfo = deviceInfo;
    this.option = DeviceProtocolBeginSealOption.getInstance(option);

    this.on = (event, fn, context) => {
      this.onFn.push({event, fn})
      this.deviceProtocol.on.call(this.deviceProtocol, event, fn, context);
    };

    this.once = (event, fn, context) => {
      this.onFn.push({event, fn})
      this.deviceProtocol.once.call(this.deviceProtocol, event, fn, context);
    }

    this.contextReset();
  }

  /**
   * 监听用印时的数据
   * onSealStatus(type:number)  1:按下  2: 上传盖印次数  3: 关仓  4: 上报异常  5:停止指纹用户用印
   * onReportDevError           通用异常
   * onAbnormalWarningEvent     用印异常
   * onDocumentStateUploading   用印数据上报
   * onStopPrivilegeSeal        停止指纹用户用印
   *
   *
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async onSealEvent({success, complete, message, fail, timeout} = {}) {
    await this._onSealEvent({success, complete, message, fail, timeout});
  }

  /**
   * 连续用印伸缩印章
   * @param out true: 伸出 false:缩回, 默认true
   * @param urgent 是否紧急开仓门 1、是 2、否
   * @param remoteLock 是否是锁定印章
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async sealStretch({out = true, urgent, remoteLock = 2, success, complete, message, fail, timeout} = {}) {
    return this._sealStretch({out, urgent, remoteLock, success, complete, message, fail, timeout});
  }

  /**
   * onFingerprintIdentify{
   *  documentId: Number, 文档id
   *  sealId: number,     印章id
   *  state: Number,      状态 0:开始认证   1:成功  2:失败  3:超时
   *  success: Boolean    是否认证成功
   * } 指纹认证, state=0 在onBeginSealed之前
   *
   * onBeginSeal          调用开始用印之前
   *
   * onBeginSealed{
   *    camera: 是否开启摄像头
   *    record: 是否录制视频
   *    analyze: 是否开启范围扫描
   *    infrared: 是否开启红外扫描
   * } 调用开始用印之后
   *
   * @final
   * @return {Promise<DeviceProtocolBeginSeal>}
   */
  async beginSeal({success, complete, message, fail, timeout} = {}) {
    if (this.isBeginSeal) {
      return this;
    }

    this.beginSealTime = Date.now();
    this.remote = this.option.remote;
    this.deviceProtocol.emit(DeviceEvent.BEFORE_BEGIN_SEAL);
    // this.send?.unsubscribe();

    //开始盖印调用
    const data = await this._beginSeal({
      success, complete, message, fail, timeout
    }).then(async data => {
      this._isBeginSeal = true;

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try { //指纹认证
          if (this.option.authentication === 1) {
            const onFingerprintIdentify = async data => {
              if (data.success) {
                await this.onSealEvent();
                this.deviceProtocol.removeListener(DeviceEvent.FINGERPRINT_IDENTIFY, onFingerprintIdentify);
                resolve(data);
              }
            };

            this.on(DeviceEvent.FINGERPRINT_IDENTIFY, onFingerprintIdentify);

            const timeout = setTimeout(() => {
              this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_IDENTIFY, {
                documentId: this.option.documentId, sealId: this.option.sealId, state: 3, success: false
              });

              this.deviceProtocol.removeListener(DeviceEvent.FINGERPRINT_IDENTIFY, onFingerprintIdentify);
            }, this.option.fingerprintIdentifyTimeout);

            this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_IDENTIFY, {
              documentId: this.option.documentId, sealId: this.option.sealId, state: 0, success: false, cancel: () => {
                clearTimeout(timeout);
                this.deviceProtocol.send.cancelOperation({type: 1});
                this.deviceProtocol.removeListener(DeviceEvent.FINGERPRINT_IDENTIFY, onFingerprintIdentify);
              }
            });

            await this._fingerprintIdentify().then(() => {
              clearTimeout(timeout);
            });
          } else {
            await this.onSealEvent();
            resolve(data);
          }
        } catch (e) {
          reject(WebError.error(e, deviceError.FINGERPRINT_AUTH_ERROR));
          this._isBeginSeal = false;
        }
      })
    });

    this._isBeginSeal = true;

    if (data.success) {
      this._sendSealAuthentication();
    }

    //开始盖印结束
    this.deviceProtocol.emit(DeviceEvent.BEGIN_SEAL, this.context);

    return this;
  }

  async clickResult({status, desc, success, complete, message, fail, timeout}) {
    return this._clickResult({status, desc, success, complete, message, fail, timeout});
  }

  async _clickResult({status, desc, success, complete, message, fail, timeout}) {

  }

  /**
   * onEquipmentMoveEvent(documentRemoteId)       异常移动时调用,documentRemoteId:审批id
   * onEquipmentUnlock(documentRemoteId， move)   lock后解锁既会调用, documentRemoteId:审批id  move:是否移动解锁
   * onDocumentRemoteStateUploading(params);      当前远程用印的盖印数据，params用印数据, 补充documentRemoteId
   *
   *
   * @final
   * @param documentRemoteId  审批id
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{
   *    documentRemoteId?: Number,
   *    cancel: function(boolean?):Promise<void>
   *    upload: function({}):Promise<*>
   *     examineResult?: function({documentRemoteId, state})
   * }>} 取消盖印时调用改方法
   *
   * examineResult ocr与qrcode的情况才返回
   */
  async equipmentLock({
                        sendEquipmentLock, upload, success, complete, message, fail, timeout
                      } = {}) {
    if (!this._context.equipmentLock) {
      this._context.equipmentLock = true;

      //解锁后取消锁定状态
      this.once(DeviceEvent.EQUIPMENT_UNLOCK, () => {
        this._context.equipmentLock = false;
      });

      let equipmentLock;
      if (this.remote === DeviceBeginSealType.remote) {
        equipmentLock = await this._equipmentLock({
          sendEquipmentLock, success, complete, message, fail, timeout
        });

        equipmentLock.upload = upload;
      } else if (this.deviceInfo.sealAuthentication) {
        equipmentLock = await this._equipmentLockOcr({
          sendEquipmentLock, success, complete, message, fail, timeout
        });

        equipmentLock.upload = upload;
      } else {
        equipmentLock = {documentRemoteId: 0};
      }


      return equipmentLock;
    } else {
      const err = WebError.code(defaultError.DEVICE_LOCKED);
      fail?.call(this, err);
      throw err;
    }
  }

  /**
   * 设备锁定解锁指令
   *
   * @param documentId 文档id
   * @param lock        true: 锁定  false: 解锁
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async remoteLock({lock, success, complete, message, fail, timeout} = {}) {
    const documentId = this.deviceInfo.documentId;
    const _remoteLockStatus = !!lock;

    if (!_remoteLockStatus && this.deviceInfo.leftTimes <= 0) {
      return;
    }

    if (this._isBeginSeal && this._remoteLockStatus !== _remoteLockStatus) {
      lock = _remoteLockStatus;

      if (lock) {
        this.lockTime = new Date().getTime();
      } else {
        this.unlockTime = new Date().getTime();
        this.lockTime = null;
        this.beforeLockTime = null;
      }

      if (this.context.data.exec instanceof Function) {
        if (!lock) {
          this.context.data.exec.call();
          this.context.data.exec = null;
          this.remoteLockStatus = lock;
        }
      } else {
        if (this.deviceInfo.remote === DeviceBeginSealType.continuous) {
          await this.sealStretch({out: !lock, remoteLock: 1, success, complete, message, fail, timeout})
        } else {
          await this._remoteLock({documentId, lock, remoteLock: 1, success, complete, message, fail, timeout});
        }
        this.remoteLockStatus = lock;
      }


      return true;
    }

    return false;
  }

  /**
   * 结束用印
   * @final
   * @param force
   * @param logout
   * @param type
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async endSeal({force = false, logout = false, type = 1, success, complete, message, fail, timeout} = {}) {
    const clear = () => {
      if (this._isBeginSeal) {
        this.deviceProtocol.emit(DeviceEvent.END_SEAL, {
          type, exit: 1
        });
      }

      this.onFn.forEach(event => {
        this.deviceProtocol.removeListener(event.event, event.fn);
      })

      this.beginSealTime = null;
      this._isBeginSeal = false;
      this._mode = null;
      this.contextReset();

      if (this.deviceProtocol.send) {
        this.deviceProtocol.send.unsubscribe();
      }
    };

    if (!this.deviceProtocol.deviceConnectProvide.connected || logout) {
      clear();
    } else if (this._isBeginSeal) {
      return await this._endSeal({success, complete, message, fail, timeout}).catch(e => {
        if (force) {
          return {};
        } else {
          throw e;
        }
      }).then(data => {
        clear();
        return data;
      });
    }
  }

  /**
   * 认证模式下, 下发盖印次数
   * @final
   * @param documentRemoteId  审批od
   * @param state             审批状态
   * @param directUse
   * @param mode
   * 下发的模式  同意之后盖印模式
   * 1、常规盖印一审一印模式
   * 2、常规盖印一审多印模式
   * 3、连续盖印一审多印模式
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   */
  async examineResult(documentRemoteId, state, {
    directUse = false, mode = 1, success, complete, message, fail, timeout
  } = {}) {
    const examineResult = async (type) => {
      await this._examineResult(documentRemoteId, state, {
        type, mode, success: () => {
          if (state === 1) {
            this._mode = mode;
            this._context.isExamine = true;
            this._context.mode = mode;
          }
          success?.call(this);
        }, complete, message, fail, timeout
      });

      if (this.option.autoSealStretch && mode === 3 && state === 1) {
        await this.sealStretch({out: true});
      }
    }

    if (this._isBeginSeal && !this._context.isExamine && (!this._mode || this._mode === 1)) {
      //当时全量释放时, 设备检验结束
      if (this.deviceInfo.fullExamine) {
        this.deviceInfo.setExamineFinish();
      }
    } else {
      throw WebError.code(defaultError.EXECUTION_FAILURE, {
        msg: !this._isBeginSeal ? "已经结束用印不允许调用" : !(this._mode && this._mode === 1) ? "多用印模式, 不因该调用" : "正在落印阶段"
      });
    }

    if ((this.deviceProtocol.deviceInfo.sealDiscern === 1 && this.deviceProtocol.deviceInfo.remote !== DeviceBeginSealType.remote) || directUse) {
      this.remoteLockStatus = true;

      this.context.data.exec = () => {
        return examineResult(1);
      }
    } else {
      this.remoteLockStatus = false;
      return examineResult(2);
    }
  }

  /**
   * 发起印章认证
   * @final
   */
  _sendSealAuthentication() {
    if ((this.option.sealAuthentication === 1 || this.option.remote === DeviceBeginSealType.remote) && this.deviceInfo.leftTimes > 0) {
      this.deviceProtocol.emit(DeviceEvent.SEAL_AUTHENTICATION, {
        remote: this.option.remote
      });
    }
  }

  /**
   * 重置context
   * @final
   */
  _contextReset(send = false) {
    this._context = {};
    this._context.isExamine = false;
    this._context.equipmentLock = false;
    this._context.mode = this._mode;
    this._context.data = {};

    if (send) {
      this._sendSealAuthentication();
    }
  }

  contextReset(send = false) {
    this._contextReset(send);
  }

  /**
   *
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _onSealEvent({success, complete, message, fail, timeout} = {}) {
  }

  /**
   * 认证模式下, 下发盖印次数
   * @abstract
   * @param documentRemoteId  审批od
   * @param state             审批状态
   * @param mode
   * 下发的模式  同意之后盖印模式
   * 1、常规盖印一审一印模式
   * 2、常规盖印一审多印模式
   * 3、连续盖印一审多印模式
   * @param type 类型 1: 范围识别  2:正常用印
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   */
  async _examineResult(documentRemoteId, state, {mode, type, success, complete, message, fail, timeout} = {}) {
  }

  /**
   * onEquipmentMoveEvent(documentRemoteId)       异常移动时调用,documentRemoteId:审批id
   * onEquipmentUnlock(documentRemoteId， move)   lock后解锁既会调用, documentRemoteId:审批id  move:是否移动解锁
   * onDocumentRemoteStateUploading(params);      当前远程用印的盖印数据，params用印数据, 补充documentRemoteId
   *
   *
   * @abstract
   * @param documentRemoteId  审批id
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{documentRemoteId: Number, cancel: function(Number):Promise<void>}>}
   */
  async _equipmentLock({
                         sendEquipmentLock, success, complete, message, fail, timeout
                       } = {}) {
  }

  /**
   *
   *
   * @param sendEquipmentLock
   * @param cancel
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{documentRemoteId: Number, cancel: function(boolean):Promise<void>}>}
   */
  async _equipmentLockOcr({
                            sendEquipmentLock, cancel, success, complete, message, fail, timeout
                          } = {}) {
    const data = await sendEquipmentLock?.call(this);
    const documentRemoteId = data ? data.documentRemoteId : new Date().getTime() + "" + Math.floor(Math.random() * 10) + 1;

    let clear = false;

    const onDocumentStateUploading = params => {
      params.documentRemoteId = documentRemoteId;
      this.deviceProtocol.emit(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, params);
    };

    /**
     *
     * @param type 1:正常结束  2:移动取消  3:app取消
     * @return {Promise<void>}
     */
    const clearMoveEvent = async (type = 3) => {
      if (!clear) {
        clear = true;
        if (type === 2) {
          this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_MOVE, {documentRemoteId});
        } else if (type === 3) {
          await this._remoteUnlock(documentRemoteId, type);
        }

        this.deviceProtocol.removeListener(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, onDocumentStateUploading);
        this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_UNLOCK, {documentRemoteId, type: type});
      }
    };

    this.once(DeviceEvent.DOCUMENT_STATE_UPLOADING, onDocumentStateUploading);

    return {
      documentRemoteId, cancel: clearMoveEvent, examineResult: async ({state}) => {
        await this.examineResult(documentRemoteId, state, {mode: this.deviceInfo.mode});

        this.once(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, () => {
          clearMoveEvent(1);
        });
      }
    };
  }

  async _remoteUnlock(documentRemoteId, type) {
  }

  /**
   * 监听指纹认证
   * @abstract
   * @return {Promise<*>}
   */
  async _fingerprintIdentify({success, complete, message, fail, timeout} = {}) {
  }

  /**
   *
   * @abstract
   * onFingerprintIdentify{
   *  documentId: Number, 文档id
   *  sealId: number,     印章id
   *  state: Number,      状态 0:开始认证   1:成功  2:失败  3:超时
   *  success: Boolean    是否认证成功
   * } 指纹认证, state=0 在onBeginSealed之前
   *
   * onBeginSeal          调用开始用印之前
   *
   * onBeginSealed{
   *    camera: 是否开启摄像头
   *    record: 是否录制视频
   *    analyze: 是否开启范围扫描
   *    infrared: 是否开启红外扫描
   * } 调用开始用印之后
   *
   * @return {Promise<*>}
   */
  async _beginSeal({success, complete, message, fail, timeout} = {}) {
  }

  /**
   * 连续用印社畜印章
   * @abstract
   * @param out true: 伸出 false:缩回, 默认true
   * @param urgent 是否紧急开仓门 1、是 2、否
   * @param remoteLock 是否是锁定印章
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async _sealStretch({out = true, urgent, remoteLock = 2, success, complete, message, fail, timeout} = {}) {
  }

  /**
   * 设备锁定解锁指令
   *
   * @abstract
   * @param documentId 文档id
   * @param lock        true: 锁定  false: 解锁
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async _remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
  }

  /**
   * 结束用印
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async _endSeal({success, complete, message, fail, timeout} = {}) {
  }
}

/**
 * 1、取消盖印
 *
 * 2、取消换章
 *
 * 3、取消指纹录入
 *
 * 4、取消加墨
 */
class DeviceProtocolOperation {
  /**

   /**
   * @type {DeviceProtocol | MqttDeviceProtocol | BleDeviceProtocol}
   * @private
   */
  deviceProtocol;

  /**
   * @type {DeviceInfo}
   * @private
   */
  deviceInfo;

  _isBeginSeal = false;

  get isBeginSeal() {
    return this._isBeginSeal;
  }

  /**
   *
   * @param deviceInfo{DeviceInfo}
   * @param deviceProtocol{DeviceProtocol}
   */
  constructor(deviceInfo, deviceProtocol) {
    this.deviceProtocol = deviceProtocol;
    this.deviceInfo = deviceInfo;
    this.on = this.deviceProtocol.on.bind(this.deviceProtocol);
    this.once = this.deviceProtocol.once.bind(this.deviceProtocol);
  }

  async beginSeal() {
    this._isBeginSeal = true;
  }

  async endSeal({force = false, logout = false, type = 1, success, complete, message, fail, timeout} = {}) {
    if (this._isBeginSeal) {
      this._isBeginSeal = false;

      if (this.deviceProtocol.send) {
        await this.deviceProtocol.send.unsubscribe();
      }

      this.deviceProtocol.emit(DeviceEvent.END_SEAL, {
        type, exit: 2
      });
    }
  }

  /**
   * 更换印章
   *
   * @param sealId
   * @param sealName
   * @param motion 1、更换印章 2、卸下印章
   * @param type
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{
   *     cancel: (function({success: *, complete: *, message: *, fail: *, timeout: *}=): Promise<*>), //取消更换印章
   *     finish: (function({success: *, complete: *, message: *, fail: *, timeout: *}=): Promise<*>)  //完成更换印章
   * }>}
   */
  async changeSeal({oldSealId,sealId, motion, sealName, type, success, complete, message, fail, timeout} = {}) {
    await this._changeSeal({sealId, motion, sealName, type, success, complete, message, fail, timeout});
    if (motion == 2) {
      this._operationLog(84,{sealId,sealName,isPre:true});
    } else if (motion == 1) {
      this._operationLog(83,{oldSealId,sealId,sealName,isPre:true});
    }
    return {
      finish: ({success, complete, message, fail, timeout} = {}) => {
        return this._changeSealFinish({
          sealId, motion, sealName, type, success, complete, message, fail, timeout
        }).then(data => {
          if (motion == 2) {
      this._operationLog(84,{sealId,sealName,isPre:false});
    } else if (motion == 1) {
      this._operationLog(83,{oldSealId,sealId,sealName,isPre:false});
    }
          this.deviceInfo.sealId = sealId;
          this.deviceInfo.sealName = sealName;
          return data;
        });
      }, cancel: ({success, complete, message, fail, timeout} = {}) => {
        return this._cancelOperation({type: 2, success, complete, message, fail, timeout});
      }
    };
  }

  /**
   * 加墨
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{
   *     cancel: (function({success: *, complete: *, message: *, fail: *, timeout: *}=): Promise<*>), //取消加墨
   *     finish: (function({success: *, complete: *, message: *, fail: *, timeout: *}=): Promise<*>)  //完成加墨
   * }>}
   */
  async addInk({success, complete, message, fail, timeout} = {}) {
    await this._beginInk({success, complete, message, fail, timeout});
    this._operationLog(2,{isPre:true});
    return {
      finish: ({success, complete, message, fail, timeout} = {}) => {
        return this._beginInkResult({success, complete, message, fail, timeout}).then(data => {
          this._operationLog(2);
          return data;
        });
      }, cancel: ({success, complete, message, fail, timeout} = {}) => {
        return this._cancelOperation({type: 4, success, complete, message, fail, timeout});
      }
    };
  }

  async _operationLog(logType) {
  }

  /**
   * 获取服务器信息
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async envinfo({success, complete, message, fail, timeout} = {}) {
    return this._envinfo({success, complete, message, fail, timeout});
  }

  /**
   * 蘸墨
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async withInk({success, complete, message, fail, timeout} = {}) {
    return this._withInk({success, complete, message, fail, timeout}).then(data => {
      this._operationLog(1)
      return data;
    });
  }

  /**
   * 切换环境
   * @param api {string} 接口域名，例如 `https://uteydaapi.libawall.com`
   * @param iot {string} 物联网地址，例如 `115.238.152.230:1887`
   * @param clear {number} 是否清空配置，1删除，2不删除
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   */
  async envChange({api, iot, type, clear, success, complete, message, fail, timeout} = {}) {
    return this._envChange({api, iot, type, clear, success, complete, message, fail, timeout});
  }

  //usbMode模式切换
  async connectChange({usbMode, success, complete, message, fail, timeout} = {}) {
    return this._connectChange({usbMode, success, complete, message, fail, timeout});
  }

  /**
   * onFingerprintType 三次录入完成
   * onFingerprintVerify 指纹认证
   * onFingerprintFinish 录入完成结束
   *
   * 录入指纹
   *
   * @param type
   * @param valida
   * @param staffId
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<
   *                 {
   *                    cancel: (function({success: *, complete: *, message: *, fail: *, timeout: *}=): Promise<*>),
   *                    finish: (function({success: *, complete: *, message: *, fail: *, timeout: *}=): Promise<*>),
   *                    type: number,
   *                    position: number,
   *                    auth: number
   *                }
   */
  async fingerprintOperation({
                               type,
                               valid = false,
                               auth = 2,
                               staffId,
                               userName,
                               success,
                               complete,
                               message,
                               fail,
                               timeout
                             } = {}) {
    if (type === 1) {
      return await this._fingerprintEntering({
        staffId, auth, userName, success, complete, message, fail, timeout
      });
    } else {
      return await this._fingerprintRemove({
        type, staffId, success, complete, message, fail, timeout
      });
    }
  }

  async _fingerprintRemove({staffId, success, complete, message, fail, timeout} = {}) {
  }

  /**
   * 设备唤醒
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async deviceMaintain({success, complete, message, fail, timeout} = {}) {
    return this._deviceMaintain({success, complete, message, fail, timeout});
  }

  async remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this._remoteLock({documentId, lock, success, complete, message, fail, timeout});
  }

  /**
   *
   * @param type 1:固件升级  2:镜像升级
   * @param data
   * @param localCallback
   * @param path
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{autoConnect: boolean, reconnect: function:Promise<void>}>}
   */
  async firmwareUpgrade({type = 1, data, localCallback, path, success, complete, message, fail, timeout} = {}) {
    return this._firmwareUpgrade({type, data, localCallback, path, success, complete, message, fail, timeout});
  }

  /**
   * 设备唤醒
   * @param state 播放状态:1:开始播放,2:播放结束
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<*>}
   */
  async singing({state, success, complete, message, fail, timeout} = {}) {
    return this._singing({state, success, complete, message, fail, timeout});
  }

  async release() {

  }

  /**
   * 影像同步
   * @param onDownloadFile{function({filePath: String, entry: null||AdbSyncEntry, data: String||File}[])}
   * @return {Promise<void>}
   */
  async fileSync(onDownloadFile) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

  /**
   *
   * @param onWifi
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {
   *   Promise<{
   *   setting: function(params:{type, save, ssid, password, success, complete, message, fail, timeout}),
   *   cancel: function,
   *   onWifi: boolean
   * }>
   * }
   * setting: 设置wifi
   *
   * cancel: 取消设置wifi
   *
   * onWifi: 是否支持扫描wifi
   */
  async wifiList({onWifi, success, complete, message, fail, timeout}) {
    return this._wifiList({onWifi, success, complete, message, fail, timeout});
  }

  /**
   *
   * @param type
   * @param save
   * @param name
   * @param password
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {*}
   */
  async wifiSetting({type, save, ssid, password, success, complete, message, fail, timeout}) {
    return this._wifiSetting({
      type, save, ssid, password, success, complete, message, fail, timeout
    }).then(data => {
      this._operationLog(3)
      return data;
    })
  }


  /**
   *
   * @abstract
   * @param type
   * @param save
   * @param ssid
   * @param password
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{status, message}>}
   */
  async _wifiSetting({type, save = 1, ssid, password, success, complete, message, fail, timeout}) {
  }

  /**
   * @abstract
   * @param onWifi
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @private
   */
  async _wifiList({onWifi, success, complete, message, fail, timeout}) {
  }

  /**
   * @abstract
   * @param state
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _singing({state, success, complete, message, fail, timeout} = {}) {
  }

  /**
   *
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _deviceMaintain({success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param documentId
   * @param lock
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
  }

  /**
   *
   * @abstract
   * @param staffId
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{type:number, position:number, auth:number, cancel: function()}>}
   * @private
   */
  async _fingerprintEntering({staffId, success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @return {Promise<{
   *     cancel: function() //取消订阅
   * }>}
   * @private
   */
  async _onFingerprint(type, position, auth) {

  }

  /**
   *
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _envinfo({success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _withInk({success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param api
   * @param iot
   * @param type
   * @param clear
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _envChange({api, iot, type, clear, success, complete, message, fail, timeout} = {}) {

  }


  async _connectChange({usbMode, success, complete, message, fail, timeout} = {}) {

  }
  /**
   *
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _beginInk({success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _beginInkResult({success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param type
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _cancelOperation({type, success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param sealId
   * @param sealName
   * @param type
   * @param motion
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _changeSealFinish({sealId, sealName, type, motion, success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param sealId
   * @param sealName
   * @param motion
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<void>}
   * @private
   */
  async _changeSeal({sealId, sealName, motion, success, complete, message, fail, timeout} = {}) {

  }

  /**
   *
   * @abstract
   * @param data
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<{autoConnect: boolean, reconnect: Function}>}
   * @private
   */
  async _firmwareUpgrade({data, success, complete, message, fail, timeout} = {}) {
  }
}

/**
 */
const DeviceEvent = {
  CONNECT: "connect",
  CONNECTED: "connected",
  DISCONNECT: "disconnect",
  DISCONNECTED: "disconnected",
  ERROR: "error",
  RECONNECT: "onReconnect",
  TIMEOUT: "timeout",

  /**
   * 属性上报
   *
   * @type {string}
   */
  PROPERTY: "property",

  /**
   * 设备上线
   * @type {string}
   */
  ONLINE: "online",

  /**
   * 设备下线
   * @type {string}
   */
  OFFLINE: "offline",

  /**
   * 用户登录前
   *
   * @type {string}
   */
  BEFORE_USER_LOGIN: "beforeUserLogin",

  /**
   * 登录
   * @type {string}
   */
  USER_LOGIN: "userLogin",

  /**
   * 指纹认证结果
   * @param parmas{{
   *   documentId,
   *   sealId,
   *   state,
   *   success
   * }}
   * documentId{Number} 文档id
   *
   * sealId{number}     印章id
   *
   * state{Number}      状态 0:开始认证   1:成功  2:失败  3:超时
   *
   * success{Boolean}   是否认证成功
   *
   * {string}
   */
  FINGERPRINT_IDENTIFY: "fingerprintIdentify",


  /**
   * 三次录入结束
   *
   * @type {string}
   */
  FINGERPRINT_TYPE: "fingerprintType",

  /**
   * 指纹认证
   *
   * @type {string}
   */
  FINGERPRINT_VERIFY: "fingerprintVerify",


  /**
   * 上传完成后, 指纹录入完成
   *
   * @type {string}
   */
  FINGERPRINT_FINISH: "fingerprintFinish",

  /**
   * 开始用印前
   *
   * @type {string}
   */
  BEFORE_BEGIN_SEAL: "beforeBeginSeal",

  /**
   * 开始用印后
   *
   * @type {string}
   */
  BEGIN_SEAL: "beginSeal",

  /**
   * 用印认证监听
   * @param params{
   *   remote
   * }
   *
   * @type {string}
   */
  SEAL_AUTHENTICATION: "sealAuthentication",


  /**
   * 盖印用印上传, 与documentStateUploading一致
   * @type {string}
   */
  DOCUMENT_STATE_UPLOADING: "documentStateUploading",

  /**
   * 远程盖印用印上传, 与documentStateUploading一致
   * @type {string}
   */
  DOCUMENT_REMOTE_STATE_UPLOADING: "documentRemoteStateUploading",

  /**
   * 远程审批移动
   * @param params{
   *   documentRemoteId
   * }
   * documentRemoteId //远程审批id
   * @type {string}
   */
  EQUIPMENT_MOVE: "equipmentMoveEvent",

  /**
   * 连续用印伸缩印章
   * sealStatus(out, urgent)
   *
   * @param params{{
   *   out,
   *   urgent
   * }}
   * out:true: 伸出 false:缩回, 默认true
   *
   * urgent:是否紧急开仓门 1、是 2、否
   *
   * @type {string}
   */
  SEAL_STRETCH: "sealStretch",

  /**
   * @param params{
   *   status:number
   * }
   * status:1:按下  2: 上传盖印次数  3: 关仓  4: 上报异常  5:停止指纹用户用印
   *
   * @type {string}
   */
  SEAL_STATUS: "sealStatus",

  /**
   * onReportDevError           通用异常
   *
   * @type {string}
   */
  REPORT_DEV_ERROR: "reportDevError",

  /**
   * onAbnormalWarningEvent     用印异常
   * @type {string} 4: 印章异常预警   11: 超时按压预警
   */
  ABNORMAL_WARNING: "abnormalWarningEvent",

  /**
   * 停止指纹用户用印
   *
   * @type {string}
   */
  STOP_PRIVILEGE_SEAL: "stopPrivilegeSeal",

  /**
   * 设备锁定
   * @param params{{documentRemoteId}} 远程审批id
   *
   * @type {string}
   */
  EQUIPMENT_LOCK: "equipmentLock",

  /**
   * 设备解锁
   *
   * @param params{{
   *   documentRemoteId,
   *   type
   * }}
   * documentRemoteId: 远程审批id
   *
   * type:1:正常结束  2:移动取消  3:app取消
   *
   * @type {string}
   */
  EQUIPMENT_UNLOCK: "equipmentUnlock",

  /**
   * 结束用印
   * @param params{{
   *   type:number,
   *   exit:number
   * }}
   * type 1: 正常退出, 2: 超时退出  3: 断开连接 4: 心跳超时  5: 权限被取消, 停止用印   6: deviceInfo被替换, 一个协议只能被一个用户使用, 代码无异常的情况不会出现此情况
   *
   * exit:1: 设备用印 2: 设备操作
   *
   * @type {string}
   */
  END_SEAL: "endSeal",

  /**
   * 固件升级进度
   *
   * @param params{{
   *   setp:number,
   *   progress:number,
   *   desc:string
   * }}
   *
   * setp 进度 1: 下载中 2: 传输完成设备重启 3:升级完成 。-1：升级失败。-2：下载失败。-3：校验失败。-4：烧写失败。
   *
   * progress 当前阶段进度
   *
   * desc 错误信息
   *
   * @type {string}
   */
  OTA_PROGRESS: "otaProgress",

  /**
   * 退出登录
   * @param params{{
   *  type:number
   * }}
   * type 1: 正常退出, 2: 超时退出  3: 主动断开连接 4: 心跳超时
   */
  USER_LOGOUT: "userLogout",

  /**
   * 退出登录
   * @param params{{
   *   progress:number,
   * }}
   */
  SYNC_STATUS: "syncStatus",

  /**
   * 退出登录
   * @param params{{
   *   lock:boolean,
   *   documentId:Number
   * }}
   */
  REMOTE_LOCK: "remoteLock",

  DOCUMENT_FILE_UPLOAD: "documentFileUpload"
};

export {
  DeviceProtocol,
  DeviceProtocolBeginSeal,
  DeviceProtocolOption,
  DeviceProtocolOperation,
  DeviceEvent,
  CompositeDeviceProtocol,
  DeviceBeginSealType,
  defaultResult
};